import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;

const ManageHomepageImagesPage = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    image_url: '',
    title: ''
  });

  useEffect(() => {
    verifyAdminAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAdminAndFetch = async () => {
    const isAdmin = localStorage.getItem('isAdminAuthenticated');
    if (!isAdmin) {
      toast.error('Unauthorized access');
      navigate('/admin/login');
      return;
    }
    await fetchImages();
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/homepage-images`);
      setImages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          image_url: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    
    if (!formData.image_url) {
      toast.error('Please upload an image');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/homepage-images`, formData);
      
      if (images.length >= MAX_IMAGES) {
        toast.success('Image added! Oldest image removed to maintain limit of 5.');
      } else {
        toast.success('Image added successfully!');
      }
      
      setShowAddModal(false);
      setFormData({ image_url: '', title: '' });
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add image');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (image) => {
    setSelectedImage(image);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/homepage-images/${selectedImage.id}`);
      toast.success('Image deleted successfully!');
      setShowDeleteModal(false);
      setSelectedImage(null);
      fetchImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleBack = () => {
    navigate('/admin/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <ImageIcon className="h-7 w-7 text-purple-600" />
                  Manage Homepage Images
                </h1>
                <p className="text-sm text-gray-600">Upload and manage carousel images (Max: {MAX_IMAGES})</p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              disabled={images.length >= MAX_IMAGES && !showAddModal}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Image
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Info Banner */}
        {images.length >= MAX_IMAGES && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-orange-800">Maximum limit reached</p>
              <p className="text-xs text-orange-700 mt-1">
                You have {MAX_IMAGES} images. Adding a new image will automatically remove the oldest one.
              </p>
            </div>
          </div>
        )}

        {images.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Images Yet</h3>
            <p className="text-gray-500 mb-6">Add your first image to get started with the homepage carousel</p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add First Image
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image, index) => (
              <Card key={image.id} className="shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative h-48 bg-gray-100">
                  <img
                    src={image.image_url}
                    alt={image.title || 'Homepage image'}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                    #{index + 1}
                  </div>
                </div>
                <CardContent className="pt-4">
                  <div className="mb-3">
                    <p className="text-sm text-gray-500 mb-1">Title</p>
                    <p className="font-semibold text-gray-900">
                      {image.title || <span className="text-gray-400 italic">No title</span>}
                    </p>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500">
                      Uploaded: {new Date(image.upload_date).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteClick(image)}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Image Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Homepage Image</DialogTitle>
            <DialogDescription>
              Upload an image for the homepage carousel slider
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddImage} className="space-y-4">
            <div>
              <Label htmlFor="image">Image *</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="cursor-pointer"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Max size: 2MB. Recommended: 1920x600px</p>
              {formData.image_url && (
                <div className="mt-3">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Annual Sports Day 2025"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ image_url: '', title: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {uploading ? 'Uploading...' : 'Add Image'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedImage && (
            <div className="my-4">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-32 object-cover rounded-lg border"
              />
              {selectedImage.title && (
                <p className="mt-2 text-sm font-medium text-gray-900">{selectedImage.title}</p>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} className="flex-1">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageHomepageImagesPage;
